/**
 * Pure helpers for friend-accept realtime sync decisions and Discover cache reconcile.
 * Kept side-effect free for unit tests.
 */

export const APP_CONNECTION_SYNC = 'app:connection-sync';

export type ConnectionApprovalRow = {
  id?: string;
  status?: string;
  type?: string;
  user_id?: string;
  connection_user_id?: string;
};

export type AppConnectionSyncDetail = {
  reason: string;
  firstDegreeUserIds: string[];
  timestamp: number;
};

/** Whether a connections INSERT/UPDATE should hard-sync for the authenticated user. */
export function shouldHardSyncOnConnectionApproval(args: {
  currentUserId: string;
  newConnection: ConnectionApprovalRow | null | undefined;
  oldConnection?: ConnectionApprovalRow | null;
  processedIds: Set<string>;
}): boolean {
  const { currentUserId, newConnection, oldConnection, processedIds } = args;
  if (!currentUserId || !newConnection?.id) return false;
  if (processedIds.has(newConnection.id)) return false;
  if (newConnection.status !== 'ACCEPTED') return false;
  if (newConnection.type !== 'FRIEND') return false;
  if (oldConnection?.status === 'ACCEPTED') return false;

  const involvesUser =
    newConnection.user_id === currentUserId ||
    newConnection.connection_user_id === currentUserId;
  return involvesUser;
}

/** Toast/vibration only for the original request sender. Lookup failure → false (sync still runs). */
export function isApprovalToastEligible(args: {
  currentUserId: string;
  requestSenderId: string | null | undefined;
  requestLookupFailed?: boolean;
}): boolean {
  if (args.requestLookupFailed) return false;
  if (!args.currentUserId || !args.requestSenderId) return false;
  return args.requestSenderId === args.currentUserId;
}

/** Peer user IDs from refreshed first-degree NetworkConnection rows (canonical). */
export function extractFirstDegreeUserIds(
  connections: Array<{ userId?: string | null; id?: string | null }> | null | undefined
): string[] {
  if (!connections?.length) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const conn of connections) {
    const id = conn.userId || conn.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

/**
 * Canonical first-degree IDs win over Discover localStorage cache.
 * Removes only connected IDs from pending; leaves unrelated outgoing requests.
 */
export function reconcileDiscoverConnectionCache(args: {
  pendingRequestIds: string[];
  canonicalFirstDegreeUserIds: string[];
}): { connectionIds: string[]; pendingRequestIds: string[] } {
  const connectionIds = extractFirstDegreeUserIds(
    args.canonicalFirstDegreeUserIds.map((id) => ({ id }))
  );
  const connectedSet = new Set(connectionIds);
  const pendingRequestIds = (args.pendingRequestIds || []).filter(
    (id) => id && !connectedSet.has(id)
  );
  return { connectionIds, pendingRequestIds };
}
